/**
 * LineageTree — vertical chain view of lineage entries.
 *
 * Each entry shows its index, action, version, timestamp, and hash.
 * Connected by a vertical rail; the hash of each entry feeds the
 * `parent_hash` of the next, which we visualize with chevrons between
 * rows.
 */

import { useTranslation } from 'react-i18next';

import { HashDisplay } from '@/components/HashDisplay';
import type { Lineage } from '@/api/types';
import { useLocaleStore } from '@/stores/localeStore';
import { formatAbsoluteTime, relativeFromTimestamp } from '@/utils/format';

export function LineageTree({ lineage }: { lineage: Lineage }) {
  const { t } = useTranslation('verify');
  const locale = useLocaleStore((s) => s.locale);

  if (lineage.entries.length === 0) {
    return <p className="text-sm text-ink-muted">{t('lineage.empty')}</p>;
  }

  return (
    <ol className="flex flex-col gap-0">
      {lineage.entries.map((entry, i) => {
        const actionLabel =
          typeof entry.action === 'string' ? entry.action : entry.action.Custom;
        return (
          <li key={entry.index} className="relative flex items-start gap-4 py-3">
            {/* Rail */}
            {i < lineage.entries.length - 1 ? (
              <span
                aria-hidden="true"
                className="absolute start-[15px] top-10 bottom-0 w-px bg-border"
              />
            ) : null}

            <span
              aria-hidden="true"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border bg-surface text-xs font-semibold text-ink-muted"
            >
              {entry.index}
            </span>

            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-ink">{actionLabel}</span>
                <span className="font-mono text-[11px] text-ink-muted">
                  v{entry.version}
                </span>
                <span
                  className="text-[11px] text-ink-subtle"
                  title={formatAbsoluteTime(entry.signed_at.iso, locale)}
                >
                  {relativeFromTimestamp(entry.signed_at, locale)}
                </span>
              </div>
              <HashDisplay value={entry.entry_hash} />
              {entry.parent_hash ? (
                <div className="flex items-center gap-2 text-[11px] text-ink-subtle">
                  <span>{t('lineage.parent')}</span>
                  <HashDisplay value={entry.parent_hash} />
                </div>
              ) : (
                <span className="text-[11px] text-ink-subtle">{t('lineage.genesis')}</span>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
